import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import { Loader2, ArrowRight } from "lucide-react";

export default function LoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user && user.id) navigate("/app", { replace: true });
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (!res.ok) setError(res.error);
    else navigate(location.state?.from || "/app", { replace: true });
  };

  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden font-inter">
      <div className="absolute inset-0 grid-bg opacity-40 pointer-events-none" />
      <div className="absolute -top-40 -right-32 h-[500px] w-[500px] rounded-full bg-brand-blue/20 blur-3xl" />
      <div className="absolute -bottom-40 -left-32 h-[500px] w-[500px] rounded-full bg-brand-green/15 blur-3xl" />

      <div className="relative z-10 max-w-md mx-auto px-6 pt-16 pb-20">
        <Link to="/" className="inline-block mb-12">
          <Logo size="md" />
        </Link>

        <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-brand-green mb-3">
          Accès partenaires
        </div>
        <h1 className="text-4xl sm:text-5xl font-black tracking-tighter leading-none text-white">
          Connexion
        </h1>
        <p className="mt-4 text-sm text-brand-gray font-roboto leading-relaxed">
          Espace réservé. Connectez-vous pour accéder au catalogue, à votre compte ou au B2B.
        </p>

        <form onSubmit={handleSubmit} className="mt-10 space-y-4" data-testid="login-form">
          <Field
            label="E-mail"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            testId="login-email-input"
            autoFocus
          />
          <Field
            label="Mot de passe"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            testId="login-password-input"
          />

          {error && (
            <div className="text-sm text-red-400 font-roboto" data-testid="login-error">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid="login-submit-button"
            className="w-full inline-flex items-center justify-center gap-2 h-12 rounded-sm bg-brand-green text-black font-bold tracking-wide hover:bg-brand-green/90 disabled:opacity-50 transition shadow-glow-green"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Se connecter <ArrowRight className="h-4 w-4" /></>}
          </button>
        </form>

        <p className="mt-8 text-sm text-brand-gray font-roboto">
          Pas de compte ?{" "}
          <Link to="/register" className="text-brand-green hover:underline" data-testid="link-to-register">
            Créer un compte
          </Link>
        </p>
        <p className="mt-3 text-xs text-brand-gray/60 font-roboto">
          <Link to="/" className="hover:text-white" data-testid="back-to-landing">
            ← Retour à l'accueil
          </Link>
        </p>
      </div>
    </div>
  );
}

function Field({ label, testId, ...props }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-[0.25em] font-bold text-brand-gray mb-2">
        {label}
      </span>
      <input
        {...props}
        data-testid={testId}
        className="w-full h-12 px-4 bg-brand-navy/50 border border-brand-blue/60 rounded-sm text-white placeholder-white/30 focus:outline-none focus:border-brand-green focus:bg-brand-navy/80 font-roboto transition"
      />
    </label>
  );
}
